'use client';

import * as React from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { cn } from '@/lib/utils';

/**
 * Radio Group Styles Configuration
 *
 * Defines visual styles for the RadioGroup component using design tokens.
 * Supports horizontal and vertical layouts.
 */
const radioGroupStyles = {
  base: 'group',
  horizontal: 'flex flex-wrap items-center gap-4',
  vertical: 'flex flex-col gap-3',
};

const radioItemStyles = {
  base: 'group/item flex items-center gap-2',
};

const radioIndicatorStyles = {
  base: 'h-4 w-4 rounded-full border-2 border-border-primary transition-all flex items-center justify-center',
  unchecked:
    'bg-white hover:border-border-secondary dark:bg-neutral-900 dark:border-border-primary dark:hover:border-border-secondary',
  checked:
    'border-primary-500 bg-white hover:border-primary-600',
  dark: {
    unchecked: 'dark:bg-neutral-900 dark:border-border-primary',
    checked: 'dark:border-primary-500 dark:bg-white',
  },
};

const radioDotStyles = {
  base: 'h-2 w-2 rounded-full bg-primary-500 transition-opacity',
};

export interface RadioGroupProps
  extends React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root> {
  /** Layout direction: horizontal or vertical */
  layout?: 'horizontal' | 'vertical';
  /** Label for the radio group */
  label?: string;
  /** Helper text to display below the radio group */
  helperText?: string;
  /** If true, displays a required indicator */
  required?: boolean;
  /** Error message to display below the radio group */
  error?: string;
  /** Array of radio options */
  options?: Array<{
    value: string;
    label: string;
    helperText?: string;
    disabled?: boolean;
  }>;
  /** Additional CSS classes for the root */
  className?: string;
}

/**
 * RadioGroup Component
 *
 * A fully accessible radio group component using Radix UI primitives.
 * Supports horizontal and vertical layouts, labels, and helper text.
 * Integrates with design tokens for light/dark mode support.
 *
 * @example
 * ```tsx
 * // Basic radio group
 * <RadioGroup
 *   value={selected}
 *   onValueChange={setSelected}
 *   options={[
 *     { value: 'option1', label: 'Option 1' },
 *     { value: 'option2', label: 'Option 2' },
 *   ]}
 * />
 *
 * // Horizontal layout
 * <RadioGroup
 *   value={size}
 *   onValueChange={setSize}
 *   layout="horizontal"
 *   options={[
 *     { value: 'small', label: 'Small' },
 *     { value: 'medium', label: 'Medium' },
 *     { value: 'large', label: 'Large' },
 *   ]}
 * />
 *
 * // Vertical layout with helper text
 * <RadioGroup
 *   value={plan}
 *   onValueChange={setPlan}
 *   layout="vertical"
 *   label="Select a plan"
 *   options={[
 *     {
 *       value: 'free',
 *       label: 'Free Plan',
 *       helperText: 'For small projects',
 *     },
 *     {
 *       value: 'pro',
 *       label: 'Pro Plan',
 *       helperText: 'For growing teams',
 *     },
 *   ]}
 * />
 *
 * // With custom children
 * <RadioGroup value={option} onValueChange={setOption}>
 *   <RadioItem value="custom">Custom Option</RadioItem>
 *   <RadioItem value="another">Another Option</RadioItem>
 * </RadioGroup>
 *
 * // With error state
 * <RadioGroup
 *   value={answer}
 *   onValueChange={setAnswer}
 *   error="Please select an option"
 *   options={[
 *     { value: 'yes', label: 'Yes' },
 *     { value: 'no', label: 'No' },
 *   ]}
 * />
 *
 * // With react-hook-form
 * <RadioGroup {...register('preference')} layout="horizontal">
 *   <RadioItem value="option1">Option 1</RadioItem>
 *   <RadioItem value="option2">Option 2</RadioItem>
 * </RadioGroup>
 * ```
 */
const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  RadioGroupProps
>(
  (
    {
      layout = 'vertical',
      label,
      helperText,
      required,
      error,
      options,
      className,
      children,
      ...props
    },
    ref
  ) => {
    return (
      <div className="w-full">
        {/* Label */}
        {label && (
          <label className="mb-3 block text-sm font-medium text-text-primary">
            {label}
            {required && <span className="ml-1 text-error-500">*</span>}
          </label>
        )}

        {/* Radio Group */}
        <RadioGroupPrimitive.Root
          ref={ref}
          className={cn(
            radioGroupStyles.base,
            layout === 'horizontal' ? radioGroupStyles.horizontal : radioGroupStyles.vertical,
            className
          )}
          {...props}
        >
          {/* Rendered from options array */}
          {options
            ? options.map((option) => (
                <RadioItem
                  key={option.value}
                  value={option.value}
                  label={option.label}
                  helperText={option.helperText}
                  disabled={option.disabled}
                />
              ))
            : children}
        </RadioGroupPrimitive.Root>

        {/* Error Message */}
        {error && (
          <p className="mt-2 text-sm text-error-500">
            {error}
          </p>
        )}

        {/* Helper Text */}
        {helperText && !error && (
          <p className="mt-2 text-sm text-text-secondary">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

RadioGroup.displayName = 'RadioGroup';

/**
 * RadioItem Component
 *
 * Individual radio item within a radio group.
 */
export interface RadioItemProps
  extends React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item> {
  /** Label for the radio item */
  label?: string | undefined;
  /** Helper text to display below the radio item */
  helperText?: string | undefined;
}

const RadioItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  RadioItemProps
>(({ value, label, helperText, disabled, className, ...props }, ref) => (
  <div className={cn(radioItemStyles.base, disabled && 'opacity-50 cursor-not-allowed')}>
    <RadioGroupPrimitive.Item
      ref={ref}
      value={value}
      disabled={disabled}
      className={cn(
        radioIndicatorStyles.base,
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1 disabled:cursor-not-allowed',
        className
      )}
      {...props}
    >
      <RadioGroupPrimitive.Indicator className={radioDotStyles.base} />
    </RadioGroupPrimitive.Item>

    {/* Label and Helper Text */}
    {label && (
      <div className="flex flex-col">
        <label
          className="text-sm font-medium text-text-primary cursor-pointer hover:text-text-secondary transition-colors"
          onClick={(e) => {
            // This allows clicking the label to select the radio item
            e.preventDefault();
          }}
        >
          {label}
        </label>
        {helperText && (
          <p className="text-xs text-text-secondary">
            {helperText}
          </p>
        )}
      </div>
    )}
  </div>
));

RadioItem.displayName = 'RadioItem';

export { RadioGroup, RadioItem };
